<script setup>
import { ref, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import TreeSelect from 'primevue/treeselect';
import Select from 'primevue/select';
import { useRbacStore } from '@/stores/rbac.js';
import { useEmployeeStore } from '@/stores/employee.js';

const props = defineProps({
  visible: Boolean,
  mode: {
    type: String,
    default: 'create', // 'create' | 'edit'
    validator: (value) => ['create', 'edit'].includes(value)
  },
  department: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:visible', 'save', 'close']);

const rbacStore = useRbacStore();
const employeeStore = useEmployeeStore();

const form = ref({
  name: '',
  parent: null,
  head: null
});

const isEdit = computed(() => props.mode === 'edit');
const dialogTitle = computed(() => isEdit.value ? 'Редактировать отдел' : 'Создать отдел');

// Дерево отделов для TreeSelect
const departmentNodes = computed(() => {
  return convertToTreeSelectNodes(rbacStore.departmentsTree, props.department?.id);
});

function convertToTreeSelectNodes(departments, excludeId = null) {
  return departments
    .filter(dept => dept.id !== excludeId) // Исключаем текущий отдел при редактировании
    .map(dept => ({
      key: String(dept.id), // TreeSelect работает со строковыми ключами
      label: dept.name,
      children: dept.children && dept.children.length > 0
        ? convertToTreeSelectNodes(dept.children, excludeId)
        : []
    }));
}

// Список сотрудников для выбора руководителя (только активные)
const employeeOptions = ref([]);
const loadingEmployees = ref(false);

async function loadEmployeeOptions(query = '') {
  loadingEmployees.value = true;
  try {
    const response = await employeeStore.loadEmployees({
      page: 1,
      search: query,
      is_active: true
    });
    const employees = response?.results || [];
    employeeOptions.value = employees.map(emp => ({
      label: emp.full_name || `${emp.last_name} ${emp.first_name}`.trim(),
      value: emp.id
    }));
  } catch (error) {
    console.error('Ошибка загрузки сотрудников:', error);
    employeeOptions.value = [];
  } finally {
    loadingEmployees.value = false;
  }
}

watch(() => props.visible, async (newVal) => {
  if (newVal) {
    if (isEdit.value && props.department) {
      // TreeSelect ожидает объект вида {key: true} для v-model
      let parentValue = null;
      if (props.department.parent) {
        parentValue = { [props.department.parent]: true };
      }

      form.value = {
        name: props.department.name || '',
        parent: parentValue,
        head: props.department.head || null
      };
    } else {
      resetForm();
    }

    // Загружаем данные, если еще не загружены
    if (rbacStore.departments.length === 0) {
      await rbacStore.loadDepartments();
    }

    // Загружаем первую страницу сотрудников
    await loadEmployeeOptions('');

    // Если редактируем и есть руководитель, убедимся что он в списке
    if (isEdit.value && props.department?.head && props.department?.head_name) {
      const headInList = employeeOptions.value.some(emp => emp.value === props.department.head);
      if (!headInList) {
        // Добавляем текущего руководителя в начало списка
        employeeOptions.value.unshift({
          label: props.department.head_name,
          value: props.department.head
        });
      }
    }
  }
});

function resetForm() {
  form.value = {
    name: '',
    parent: null,
    head: null
  };
}

function handleSave() {
  if (!form.value.name.trim()) {
    return;
  }

  // TreeSelect возвращает объект вида {9: true} или null
  let parentId = null;
  if (form.value.parent) {
    if (typeof form.value.parent === 'object') {
      // Извлекаем ключ из объекта {9: true}
      const keys = Object.keys(form.value.parent);
      if (keys.length > 0) {
        parentId = parseInt(keys[0]);
      }
    } else {
      // Если вдруг строка или число
      parentId = parseInt(form.value.parent);
    }
  }

  const payload = {
    name: form.value.name.trim(),
    parent: parentId,
    head: form.value.head || null
  };

  emit('save', payload);
}

function handleClose() {
  emit('update:visible', false);
  emit('close');
  resetForm();
}
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :header="dialogTitle"
    modal
    :style="{ width: '600px' }"
  >
    <div class="flex flex-col gap-4">
      <!-- Название отдела -->
      <div class="flex flex-col gap-2">
        <label for="dept-name" class="font-semibold">Название отдела <span class="text-red-500">*</span></label>
        <InputText
          id="dept-name"
          v-model="form.name"
          placeholder="Введите название отдела"
          class="w-full"
        />
      </div>

      <!-- Родительский отдел -->
      <div class="flex flex-col gap-2">
        <label for="dept-parent" class="font-semibold">Родительский отдел</label>
        <TreeSelect
          id="dept-parent"
          v-model="form.parent"
          :options="departmentNodes"
          placeholder="Выберите родительский отдел"
          class="w-full"
          :filter="true"
          :showClear="true"
        />
      </div>

      <!-- Руководитель -->
      <div class="flex flex-col gap-2">
        <label for="dept-head" class="font-semibold">Руководитель</label>
        <Select
          id="dept-head"
          v-model="form.head"
          :options="employeeOptions"
          option-label="label"
          option-value="value"
          placeholder="Выберите руководителя"
          class="w-full"
          :filter="true"
          :showClear="true"
        />
      </div>
    </div>

    <div v-if="isEdit && department?.group_name" class="mt-2 text-sm text-gray-600">
      <strong>Группа прав отдела:</strong> {{ department.group_name }} <em>(создается автоматически)</em>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button label="Отмена" severity="secondary" @click="handleClose" rounded size="small" />
        <Button
          :label="isEdit ? 'Сохранить' : 'Создать'"
          severity="info"
          @click="handleSave"
          rounded
          size="small"
          :disabled="!form.name.trim()"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
</style>
